/* RevenueTrackingScreen.js — Business Revenue Management
   Tabs: Overview, Income, Invoices, Clients, Recurring, Forecast, VAT, Settings
   Features: Revenue streams, client payments, recurring billing, UK VAT tracking,
   cash flow forecast, aging reports, company logo, export */

import React, { useState, useCallback, useMemo } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, ScrollView, RefreshControl,
  StatusBar, Modal, TextInput, Alert, ActivityIndicator, Dimensions, Share, Image,
  KeyboardAvoidingView, Platform, Switch,
} from 'react-native';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width } = Dimensions.get('window');
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const api = axios.create({ baseURL: API_BASE_URL, timeout: 15000, headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY } });
api.interceptors.request.use(async c => { try { const t = await AsyncStorage.getItem('auth_token'); if (t) c.headers.Authorization = 'Bearer ' + t; } catch {} return c; });
const safeData = r => (r?.data?.success && Array.isArray(r.data.data)) ? r.data.data : [];

const fmtGBP = (v, d = 2) => '£' + (parseFloat(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtK = v => { const n = parseFloat(v) || 0; return n >= 1e6 ? '£' + (n / 1e6).toFixed(1) + 'M' : n >= 1e3 ? '£' + (n / 1e3).toFixed(1) + 'K' : '£' + n.toFixed(0); };
const fmtPct = v => (parseFloat(v) || 0).toFixed(1) + '%';
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const C = { primary: '#0F172A', accent: '#059669', blue: '#3B82F6', danger: '#DC2626', warning: '#D97706', purple: '#7C3AED', pink: '#EC4899', teal: '#0D9488', orange: '#EA580C', slate: '#64748B' };

// ── Revenue Categories ───────────────────────────────────────────────────
const REVENUE_SOURCES = [
  { id: 'services', label: 'Services', icon: 'briefcase-check', color: '#059669', desc: 'Professional & consulting services' },
  { id: 'products', label: 'Product Sales', icon: 'package-variant', color: '#3B82F6', desc: 'Physical or digital product sales' },
  { id: 'subscriptions', label: 'Subscriptions', icon: 'autorenew', color: '#7C3AED', desc: 'Recurring subscription revenue' },
  { id: 'licensing', label: 'Licensing & Royalties', icon: 'license', color: '#EC4899', desc: 'IP licensing and royalty income' },
  { id: 'recruitment', label: 'Recruitment Fees', icon: 'account-search', color: '#0EA5E9', desc: 'Placement and recruitment fees' },
  { id: 'consulting', label: 'Consulting', icon: 'head-lightbulb', color: '#F59E0B', desc: 'Advisory and consultancy' },
  { id: 'project', label: 'Project Revenue', icon: 'clipboard-list', color: '#14B8A6', desc: 'Project-based income' },
  { id: 'retainer', label: 'Retainer Fees', icon: 'calendar-check', color: '#6366F1', desc: 'Monthly/quarterly retainers' },
  { id: 'commission', label: 'Commissions', icon: 'percent-circle', color: '#EA580C', desc: 'Sales commissions earned' },
  { id: 'grants', label: 'Grants & Funding', icon: 'bank', color: '#0891B2', desc: 'Government grants, R&D tax credits' },
  { id: 'interest', label: 'Interest Income', icon: 'chart-line-variant', color: '#4338CA', desc: 'Bank interest and investments' },
  { id: 'rental', label: 'Rental Income', icon: 'home-city', color: '#B45309', desc: 'Property or equipment rental' },
  { id: 'other', label: 'Other Income', icon: 'dots-horizontal-circle', color: '#94A3B8', desc: 'Miscellaneous revenue' },
];

const INV_STATUS = {
  draft: { c: '#94A3B8', bg: '#F1F5F9', l: 'Draft', i: 'pencil' },
  sent: { c: '#3B82F6', bg: '#DBEAFE', l: 'Sent', i: 'email-fast' },
  viewed: { c: '#7C3AED', bg: '#EDE9FE', l: 'Viewed', i: 'eye' },
  pending: { c: '#D97706', bg: '#FEF3C7', l: 'Pending', i: 'clock-outline' },
  partial: { c: '#EA580C', bg: '#FFF7ED', l: 'Partial', i: 'cash-minus' },
  paid: { c: '#059669', bg: '#D1FAE5', l: 'Paid', i: 'check-circle' },
  overdue: { c: '#DC2626', bg: '#FEE2E2', l: 'Overdue', i: 'alert-circle' },
  cancelled: { c: '#64748B', bg: '#F1F5F9', l: 'Cancelled', i: 'close-circle' },
};

// UK VAT Rates
const VAT_RATES = { standard: 20, reduced: 5, zero: 0 };

// ══════════════════════════════════════════════════════════════════════════
export default function RevenueTrackingScreen({ navigation, route }) {
  const [companyLogo, setCompanyLogo] = useState(route?.params?.companyLogo || route?.params?.companyProfile?.logo || null);
  const [companyName, setCompanyName] = useState(route?.params?.companyName || route?.params?.companyProfile?.name || '');

  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [clients, setClients] = useState([]);
  const [revenue, setRevenue] = useState([]);

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [form, setForm] = useState({});
  const uf = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // ── Computed Stats ──
  const stats = useMemo(() => {
    const totalRev = invoices.reduce((s, i) => s + parseFloat(i.total || i.amount || i.total_amount || 0), 0);
    const paidInv = invoices.filter(i => (i.status || '').toLowerCase() === 'paid');
    const pendingInv = invoices.filter(i => ['pending', 'sent', 'viewed'].includes((i.status || '').toLowerCase()));
    const overdueInv = invoices.filter(i => (i.status || '').toLowerCase() === 'overdue');
    const partialInv = invoices.filter(i => (i.status || '').toLowerCase() === 'partial');
    const totalPaid = paidInv.reduce((s, i) => s + parseFloat(i.total || i.amount || 0), 0);
    const totalPending = pendingInv.reduce((s, i) => s + parseFloat(i.total || i.amount || 0), 0);
    const totalOverdue = overdueInv.reduce((s, i) => s + parseFloat(i.total || i.amount || 0), 0);
    const totalPartial = partialInv.reduce((s, i) => s + parseFloat(i.amount_paid || 0), 0);

    // This month
    const now = new Date();
    const thisMonth = invoices.filter(i => { const d = new Date(i.created_at || i.invoice_date || i.date); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); });
    const thisMonthRev = thisMonth.reduce((s, i) => s + parseFloat(i.total || i.amount || 0), 0);
    const lastMonth = invoices.filter(i => { const d = new Date(i.created_at || i.invoice_date || i.date); const lm = new Date(now); lm.setMonth(lm.getMonth() - 1); return d.getMonth() === lm.getMonth() && d.getFullYear() === lm.getFullYear(); });
    const lastMonthRev = lastMonth.reduce((s, i) => s + parseFloat(i.total || i.amount || 0), 0);
    const monthGrowth = lastMonthRev > 0 ? ((thisMonthRev - lastMonthRev) / lastMonthRev) * 100 : 0;

    // By source
    const bySource = {};
    invoices.forEach(i => { const src = i.revenue_source || i.category || i.service_type || 'other'; bySource[src] = (bySource[src] || 0) + parseFloat(i.total || i.amount || 0); });

    // By client
    const byClient = {};
    invoices.forEach(i => { const cl = i.client_name || i.customer_name || 'Unknown'; byClient[cl] = (byClient[cl] || 0) + parseFloat(i.total || i.amount || 0); });

    // Monthly breakdown
    const monthly = {};
    MONTHS.forEach((m, idx) => { monthly[idx] = { month: m, revenue: 0, paid: 0, count: 0 }; });
    invoices.forEach(i => { const m = new Date(i.created_at || i.invoice_date || i.date || now).getMonth(); if (monthly[m]) { monthly[m].revenue += parseFloat(i.total || i.amount || 0); monthly[m].count++; if ((i.status || '').toLowerCase() === 'paid') monthly[m].paid += parseFloat(i.total || i.amount || 0); } });

    // VAT
    const vatOutput = totalRev * 0.2; // Simplified: 20% standard rate
    const avgInvoice = invoices.length > 0 ? totalRev / invoices.length : 0;
    const collectionRate = totalRev > 0 ? (totalPaid / totalRev) * 100 : 0;

    // Recurring (subscriptions/retainers)
    const recurring = invoices.filter(i => ['subscriptions', 'retainer'].includes(i.revenue_source || i.category));
    const mrr = recurring.reduce((s, i) => s + parseFloat(i.total || i.amount || 0), 0) / Math.max(1, new Set(recurring.map(i => new Date(i.created_at || i.date).getMonth())).size);

    // Aging
    const aging = { current: 0, '30d': 0, '60d': 0, '90d': 0, '90plus': 0 };
    pendingInv.concat(overdueInv).forEach(i => {
      const days = Math.floor((now - new Date(i.due_date || i.created_at || now)) / 86400000);
      const amt = parseFloat(i.total || i.amount || 0);
      if (days <= 0) aging.current += amt;
      else if (days <= 30) aging['30d'] += amt;
      else if (days <= 60) aging['60d'] += amt;
      else if (days <= 90) aging['90d'] += amt;
      else aging['90plus'] += amt;
    });

    return {
      totalRevenue: totalRev, totalPaid, totalPending, totalOverdue, totalPartial,
      thisMonthRev, lastMonthRev, monthGrowth, invoiceCount: invoices.length,
      paidCount: paidInv.length, pendingCount: pendingInv.length, overdueCount: overdueInv.length,
      avgInvoice, collectionRate, vatOutput, mrr, bySource, byClient,
      monthly: Object.values(monthly), aging, clientCount: Object.keys(byClient).length,
    };
  }, [invoices]);

  // ── Load ──
  useFocusEffect(useCallback(() => { loadAll(); }, []));

  const loadAll = async () => {
    setIsLoading(true);
    try {
      const cid = await AsyncStorage.getItem('company_id') || '1';
      try { const c = await AsyncStorage.getItem('company_profile'); if (c) { const p = JSON.parse(c); if (p.logo) setCompanyLogo(p.logo); if (p.name || p.company_name) setCompanyName(p.name || p.company_name); } } catch {}
      try { const r = await api.get(API_BASE_URL + '/api/data/company_profiles', { params: { company_id: parseInt(cid) } }); const d = safeData(r); if (d.length > 0 && d[0].logo) setCompanyLogo(d[0].logo); if (d.length > 0) setCompanyName(d[0].name || d[0].company_name || ''); } catch {}

      const p = { company_id: parseInt(cid), limit: 500 };
      const [r1, r2, r3, r4] = await Promise.allSettled([
        api.get(API_BASE_URL + '/api/data/invoices', { params: p }).catch(() => null),
        api.get(API_BASE_URL + '/api/data/payments', { params: p }).catch(() => null),
        api.get(API_BASE_URL + '/api/data/clients', { params: p }).catch(() => null),
        api.get(API_BASE_URL + '/api/data/revenue_records', { params: p }).catch(() => null),
      ]);
      const inv = safeData(r1.status === 'fulfilled' ? r1.value : null);
      const pay = safeData(r2.status === 'fulfilled' ? r2.value : null);
      const cl = safeData(r3.status === 'fulfilled' ? r3.value : null);
      const rev = safeData(r4.status === 'fulfilled' ? r4.value : null);
      if (inv.length > 0) { setInvoices(inv); await AsyncStorage.setItem('revenue_invoices_cache', JSON.stringify(inv)); }
      else { try { const c = await AsyncStorage.getItem('revenue_invoices_cache'); if (c) setInvoices(JSON.parse(c)); } catch {} }
      setPayments(pay); setClients(cl); setRevenue(rev);
    } catch {} finally { setIsLoading(false); }
  };
  const onRefresh = async () => { setRefreshing(true); await loadAll(); setRefreshing(false); };

  // ── Record Income ──
  const saveIncome = async () => {
    if (!form.clientName) { Alert.alert('Required', 'Enter client name'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { Alert.alert('Required', 'Enter amount'); return; }
    try {
      const cid = await AsyncStorage.getItem('company_id') || '1';
      const netAmount = parseFloat(form.amount);
      const vatRate = form.vatRate || 20;
      const vatAmount = form.includeVat ? netAmount * (vatRate / 100) : 0;
      const grossAmount = netAmount + vatAmount;
      const payload = {
        invoice_number: 'INV-' + Date.now().toString(36).toUpperCase(),
        company_id: parseInt(cid), client_name: form.clientName, customer_name: form.clientName,
        client_email: form.clientEmail || '', description: form.description || '',
        amount: netAmount, total: grossAmount, total_amount: grossAmount,
        vat_amount: vatAmount, vat_rate: vatRate, currency: 'GBP',
        revenue_source: form.revenueSource || 'services', category: form.revenueSource || 'services',
        service_type: form.serviceType || '', invoice_date: form.invoiceDate || new Date().toISOString().split('T')[0],
        due_date: form.dueDate || '', payment_terms: form.paymentTerms || 'Net 30',
        status: form.markPaid ? 'paid' : 'pending', is_recurring: form.isRecurring || false,
        recurring_interval: form.recurringInterval || '', notes: form.notes || '',
        created_at: new Date().toISOString(),
      };
      try { await api.post(API_BASE_URL + '/api/data/invoices', payload); } catch {}
      const updated = [{ ...payload, id: 'inv_' + Date.now() }, ...invoices];
      setInvoices(updated); await AsyncStorage.setItem('revenue_invoices_cache', JSON.stringify(updated));
      setShowAddModal(false); setForm({});
      Alert.alert('Recorded', form.markPaid ? 'Payment recorded as paid' : 'Invoice created and sent');
    } catch (e) { Alert.alert('Error', e.message); }
  };

  // ── Update Invoice Status ──
  const updateInvoiceStatus = async (inv, newStatus) => {
    const updated = invoices.map(i => i.id === inv.id ? { ...i, status: newStatus, ...(newStatus === 'paid' ? { paid_date: new Date().toISOString() } : {}) } : i);
    setInvoices(updated);
    try { await api.put(API_BASE_URL + '/api/data/invoices/' + inv.id, { status: newStatus }); } catch {}
    await AsyncStorage.setItem('revenue_invoices_cache', JSON.stringify(updated));
    setShowDetailModal(false);
  };

  // ── Export ──
  const exportReport = () => {
    const lines = [
      `REVENUE REPORT — ${companyName}`, `Generated: ${fmtDate(new Date())}`, '',
      'SUMMARY',
      `Total Revenue: ${fmtGBP(stats.totalRevenue)}`, `Collected: ${fmtGBP(stats.totalPaid)}`,
      `Outstanding: ${fmtGBP(stats.totalPending)}`, `Overdue: ${fmtGBP(stats.totalOverdue)}`,
      `This Month: ${fmtGBP(stats.thisMonthRev)}`, `Growth: ${fmtPct(stats.monthGrowth)}`,
      `Avg Invoice: ${fmtGBP(stats.avgInvoice)}`, `Collection Rate: ${fmtPct(stats.collectionRate)}`,
      `MRR: ${fmtGBP(stats.mrr)}`, `VAT Output: ${fmtGBP(stats.vatOutput)}`,
      `Clients: ${stats.clientCount}`, '',
      'MONTHLY', ...stats.monthly.filter(m => m.revenue > 0).map(m => `${m.month}: ${fmtGBP(m.revenue)} (${m.count} inv)`),
      '', 'AGING',
      `Current: ${fmtGBP(stats.aging.current)}`, `1-30 days: ${fmtGBP(stats.aging['30d'])}`,
      `31-60 days: ${fmtGBP(stats.aging['60d'])}`, `61-90 days: ${fmtGBP(stats.aging['90d'])}`,
      `90+ days: ${fmtGBP(stats.aging['90plus'])}`,
    ];
    Share.share({ message: lines.join('\n'), title: 'Revenue Report' });
  };

  // ── Filtered ──
  const filteredInvoices = useMemo(() => {
    let list = [...invoices];
    if (activeTab === 'paid') list = list.filter(i => i.status === 'paid');
    else if (activeTab === 'outstanding') list = list.filter(i => ['pending', 'sent', 'overdue', 'partial'].includes(i.status));
    if (searchQuery) { const q = searchQuery.toLowerCase(); list = list.filter(i => JSON.stringify(i).toLowerCase().includes(q)); }
    return list.sort((a, b) => new Date(b.created_at || b.invoice_date || 0) - new Date(a.created_at || a.invoice_date || 0));
  }, [invoices, activeTab, searchQuery]);

  // ══════════════════════════════════════════════════════════════════
  // RENDERS
  // ══════════════════════════════════════════════════════════════════
  const TABS = [
    { k: 'overview', l: 'Overview', i: 'view-dashboard' },
    { k: 'income', l: 'Income', i: 'cash-plus' },
    { k: 'invoices', l: 'Invoices', i: 'file-document' },
    { k: 'clients', l: 'Clients', i: 'account-group' },
    { k: 'recurring', l: 'Recurring', i: 'autorenew' },
    { k: 'forecast', l: 'Forecast', i: 'chart-timeline-variant' },
    { k: 'vat', l: 'VAT', i: 'percent-circle' },
    { k: 'settings', l: 'Settings', i: 'cog' },
  ];

  const renderHeader = () => (
    <LinearGradient colors={['#064E3B', '#059669']} style={st.header}>
      <View style={st.headerRow}>
        <TouchableOpacity onPress={() => navigation?.goBack()} style={st.hBtn}><Ionicons name="arrow-back" size={22} color="#FFF" /></TouchableOpacity>
        {companyLogo ? <Image source={{ uri: companyLogo || undefined }} style={{ width: 28, height: 28, borderRadius: 8, marginLeft: 6 }} /> : null}
        <View style={{ flex: 1, marginLeft: 8 }}><Text style={st.hTitle}>Revenue Tracking</Text>
          <Text style={{ color: '#A7F3D0', fontSize: 11 }}>{companyName}</Text></View>
        <TouchableOpacity style={st.hBtn} onPress={exportReport}><MaterialCommunityIcons name="export-variant" size={20} color="#FFF" /></TouchableOpacity>
        <TouchableOpacity style={[st.hBtn, { backgroundColor: '#10B981', marginLeft: 6 }]} onPress={() => { setShowAddModal(true); setForm({ revenueSource: 'services', paymentTerms: 'Net 30', vatRate: 20 }); }}>
          <MaterialCommunityIcons name="plus" size={20} color="#FFF" /></TouchableOpacity>
      </View>
    </LinearGradient>);

  const renderStats = () => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={st.statsBar} contentContainerStyle={{ paddingHorizontal: 16 }}>
      {[{ l: 'Revenue', v: fmtK(stats.totalRevenue), c: C.accent, i: 'cash-multiple' },
        { l: 'Collected', v: fmtK(stats.totalPaid), c: '#10B981', i: 'check-circle' },
        { l: 'Outstanding', v: fmtK(stats.totalPending + stats.totalOverdue), c: C.warning, i: 'clock' },
        { l: 'Overdue', v: fmtK(stats.totalOverdue), c: C.danger, i: 'alert' },
        { l: 'This Month', v: fmtK(stats.thisMonthRev), c: C.blue, i: 'calendar' },
        { l: 'MRR', v: fmtK(stats.mrr), c: C.purple, i: 'autorenew' },
      ].map((s, i) => (<View key={i} style={st.statCard}><MaterialCommunityIcons name={s.i} size={16} color={s.c} />
        <Text style={[st.statVal, { color: s.c }]}>{s.v}</Text><Text style={st.statLbl}>{s.l}</Text></View>))}
    </ScrollView>);

  const renderTabs = () => (
    <View style={st.tabsRow}><ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 8 }}>
      {TABS.map(t => (<TouchableOpacity key={t.k} style={[st.tab, activeTab === t.k && st.activeTab]} onPress={() => setActiveTab(t.k)}>
        <MaterialCommunityIcons name={t.i} size={14} color={activeTab === t.k ? C.accent : '#94A3B8'} />
        <Text style={[st.tabTxt, activeTab === t.k && { color: C.accent, fontWeight: '700' }]}>{t.l}</Text></TouchableOpacity>))}
    </ScrollView></View>);

  // ── Invoice Card ──
  const InvCard = ({ inv, i }) => {
    const src = REVENUE_SOURCES.find(s => s.id === (inv.revenue_source || inv.category)) || REVENUE_SOURCES[0];
    const status = INV_STATUS[(inv.status || 'pending').toLowerCase()] || INV_STATUS.pending;
    return (<TouchableOpacity key={inv.id || i} style={st.card} onPress={() => { setSelectedItem(inv); setShowDetailModal(true); }}>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <View style={[st.cardIcon, { backgroundColor: src.color + '15' }]}><MaterialCommunityIcons name={src.icon} size={20} color={src.color} /></View>
        <View style={{ flex: 1, marginLeft: 10 }}>
          <Text style={st.cardName} numberOfLines={1}>{inv.client_name || inv.customer_name || 'Client'}</Text>
          <Text style={st.cardMeta}>{inv.invoice_number || ''} · {fmtDate(inv.invoice_date || inv.created_at)}</Text>
          {inv.description && <Text style={st.cardMeta} numberOfLines={1}>{inv.description}</Text>}
        </View>
        <View style={{ alignItems: 'flex-end' }}>
          <Text style={[st.cardAmount, { color: src.color }]}>{fmtGBP(inv.total || inv.amount)}</Text>
          <View style={[st.badge, { backgroundColor: status.bg }]}><MaterialCommunityIcons name={status.i} size={10} color={status.c} />
            <Text style={[st.badgeTxt, { color: status.c }]}>{status.l}</Text></View>
        </View>
      </View>
    </TouchableOpacity>);
  };

  // ── Tab: Overview ──
  const renderOverview = () => (<View>
    {/* Hero KPIs */}
    <View style={st.kpiRow}>
      {[{ t: 'Total Revenue', v: fmtK(stats.totalRevenue), c: C.accent, i: 'cash-multiple', sub: `${stats.invoiceCount} invoices` },
        { t: 'Collected', v: fmtK(stats.totalPaid), c: '#10B981', i: 'check-circle', sub: `${stats.paidCount} paid` },
        { t: 'Outstanding', v: fmtK(stats.totalPending), c: C.warning, i: 'clock-outline', sub: `${stats.pendingCount} pending` },
        { t: 'Overdue', v: fmtK(stats.totalOverdue), c: C.danger, i: 'alert-circle', sub: `${stats.overdueCount} overdue` },
      ].map((k, i) => (<View key={i} style={st.kpiCard}><View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
        <MaterialCommunityIcons name={k.i} size={16} color={k.c} /><Text style={{ fontSize: 10, color: '#64748B', marginLeft: 4 }}>{k.t}</Text></View>
        <Text style={[st.kpiVal, { color: k.c }]}>{k.v}</Text><Text style={{ fontSize: 10, color: '#94A3B8' }}>{k.sub}</Text></View>))}
    </View>

    {/* Overdue Alert */}
    {stats.overdueCount > 0 && <View style={st.alertCard}><MaterialCommunityIcons name="alert-circle" size={20} color={C.danger} />
      <View style={{ flex: 1, marginLeft: 10 }}><Text style={{ fontSize: 13, fontWeight: '700', color: '#7F1D1D' }}>{stats.overdueCount} Overdue — {fmtGBP(stats.totalOverdue)}</Text>
        <Text style={{ fontSize: 11, color: '#991B1B' }}>Chase these payments immediately</Text></View>
      <TouchableOpacity onPress={() => setActiveTab('invoices')}><Text style={{ color: C.danger, fontWeight: '700', fontSize: 12 }}>View</Text></TouchableOpacity></View>}

    {/* Monthly Revenue Chart */}
    <View style={st.section}>
      <Text style={st.secTitle}>Monthly Revenue</Text>
      <View style={{ flexDirection: 'row', height: 100, alignItems: 'flex-end', gap: 3, marginTop: 8 }}>
        {stats.monthly.map((m, i) => {
          const maxR = Math.max(...stats.monthly.map(d => d.revenue), 1);
          return (<View key={i} style={{ flex: 1, alignItems: 'center' }}>
            <View style={{ width: '70%', height: Math.max(2, (m.revenue / maxR) * 88), backgroundColor: m.paid > 0 ? C.accent : '#E2E8F0', borderRadius: 3 }} />
            <Text style={{ fontSize: 7, color: '#94A3B8', marginTop: 2 }}>{m.month}</Text></View>);
        })}
      </View>
    </View>

    {/* Key Metrics */}
    <View style={st.section}>
      <Text style={st.secTitle}>Performance</Text>
      {[{ l: 'Collection Rate', v: fmtPct(stats.collectionRate), c: stats.collectionRate >= 90 ? C.accent : C.warning },
        { l: 'This Month', v: fmtGBP(stats.thisMonthRev, 0), c: C.blue },
        { l: 'Month Growth', v: (stats.monthGrowth >= 0 ? '+' : '') + fmtPct(stats.monthGrowth), c: stats.monthGrowth >= 0 ? C.accent : C.danger },
        { l: 'Avg Invoice', v: fmtGBP(stats.avgInvoice, 0), c: C.purple },
        { l: 'MRR', v: fmtGBP(stats.mrr, 0), c: '#6366F1' },
        { l: 'Active Clients', v: String(stats.clientCount), c: C.teal },
        { l: 'VAT Output (20%)', v: fmtGBP(stats.vatOutput, 0), c: C.orange },
      ].map((m, i) => (<View key={i} style={st.metricRow}><Text style={st.metricLabel}>{m.l}</Text><Text style={[st.metricValue, { color: m.c }]}>{m.v}</Text></View>))}
    </View>

    {/* Revenue by Source */}
    <View style={st.section}>
      <Text style={st.secTitle}>Revenue by Source</Text>
      {Object.entries(stats.bySource).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([src, amt]) => {
        const s = REVENUE_SOURCES.find(r => r.id === src) || REVENUE_SOURCES[REVENUE_SOURCES.length - 1];
        const maxS = Object.values(stats.bySource).reduce((a, b) => Math.max(a, b), 1);
        return (<View key={src} style={{ marginBottom: 8 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}><MaterialCommunityIcons name={s.icon} size={14} color={s.color} /><Text style={{ fontSize: 12, color: '#334155' }}>{s.label}</Text></View>
            <Text style={{ fontSize: 12, fontWeight: '700', color: s.color }}>{fmtGBP(amt, 0)}</Text></View>
          <View style={{ height: 6, backgroundColor: '#E2E8F0', borderRadius: 3, overflow: 'hidden' }}>
            <View style={{ width: Math.max(3, (amt / maxS) * 100) + '%', height: '100%', backgroundColor: s.color, borderRadius: 3 }} /></View></View>);
      })}
    </View>

    {/* Aging */}
    <View style={st.section}>
      <Text style={st.secTitle}>Receivables Aging</Text>
      {[{ l: 'Current', v: stats.aging.current, c: C.accent },
        { l: '1-30 days', v: stats.aging['30d'], c: C.warning },
        { l: '31-60 days', v: stats.aging['60d'], c: C.orange },
        { l: '61-90 days', v: stats.aging['90d'], c: C.danger },
        { l: '90+ days', v: stats.aging['90plus'], c: '#7F1D1D' },
      ].map((a, i) => (<View key={i} style={st.metricRow}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}><View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: a.c }} /><Text style={st.metricLabel}>{a.l}</Text></View>
        <Text style={[st.metricValue, { color: a.c }]}>{fmtGBP(a.v, 0)}</Text></View>))}
    </View>
  </View>);

  // ── Tab: Income ──
  const renderIncome = () => (<View>
    <View style={st.kpiRow}>
      <View style={[st.kpiCard, { width: width - 32 }]}><Text style={{ fontSize: 11, color: '#64748B' }}>Total Revenue</Text>
        <Text style={[st.kpiVal, { color: C.accent, fontSize: 28 }]}>{fmtGBP(stats.totalRevenue)}</Text>
        <Text style={{ fontSize: 12, color: stats.monthGrowth >= 0 ? C.accent : C.danger, fontWeight: '700', marginTop: 4 }}>{stats.monthGrowth >= 0 ? '↑' : '↓'} {fmtPct(Math.abs(stats.monthGrowth))} vs last month</Text></View>
    </View>
    <Text style={[st.secTitle, { paddingHorizontal: 16 }]}>Revenue Sources</Text>
    {REVENUE_SOURCES.map(src => {
      const amt = stats.bySource[src.id] || 0;
      const count = invoices.filter(i => (i.revenue_source || i.category) === src.id).length;
      return (<View key={src.id} style={st.card}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <View style={[st.cardIcon, { backgroundColor: src.color + '15' }]}><MaterialCommunityIcons name={src.icon} size={20} color={src.color} /></View>
          <View style={{ flex: 1, marginLeft: 10 }}><Text style={{ fontSize: 14, fontWeight: '600', color: '#1E293B' }}>{src.label}</Text>
            <Text style={{ fontSize: 11, color: '#94A3B8' }}>{src.desc}</Text>
            {count > 0 && <Text style={{ fontSize: 10, color: '#64748B', marginTop: 2 }}>{count} transaction{count > 1 ? 's' : ''}</Text>}</View>
          <Text style={{ fontSize: 16, fontWeight: '800', color: amt > 0 ? src.color : '#CBD5E1' }}>{amt > 0 ? fmtGBP(amt, 0) : '-'}</Text>
        </View></View>);
    })}
  </View>);

  // ── Tab: Invoices ──
  const renderInvoices = () => (<View>
    <View style={st.searchRow}><MaterialCommunityIcons name="magnify" size={20} color="#9CA3AF" />
      <TextInput style={st.searchInput} placeholder="Search invoices..." placeholderTextColor="#9CA3AF" value={searchQuery} onChangeText={setSearchQuery} />
      {searchQuery ? <TouchableOpacity onPress={() => setSearchQuery('')}><MaterialCommunityIcons name="close-circle" size={18} color="#9CA3AF" /></TouchableOpacity> : null}</View>
    {/* Status filter */}
    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 16, gap: 6, marginBottom: 8 }}>
      {['all', 'paid', 'outstanding'].map(f => (<TouchableOpacity key={f} style={[st.chip, activeTab === f && st.chipActive]} onPress={() => setActiveTab(f === 'all' ? 'invoices' : f)}>
        <Text style={[st.chipTxt, activeTab === f && { color: '#FFF' }]}>{f.charAt(0).toUpperCase() + f.slice(1)}</Text></TouchableOpacity>))}
    </ScrollView>
    {filteredInvoices.length === 0 ? <View style={st.empty}><MaterialCommunityIcons name="file-document-outline" size={48} color="#CBD5E1" />
      <Text style={{ fontSize: 16, fontWeight: '700', color: '#334155', marginTop: 12 }}>No invoices found</Text></View>
    : filteredInvoices.slice(0, 30).map((inv, i) => <InvCard key={inv.id || i} inv={inv} i={i} />)}
  </View>);

  // ── Tab: Clients ──
  const renderClients = () => {
    const clientArr = Object.entries(stats.byClient).sort((a, b) => b[1] - a[1]);
    return (<View style={{ padding: 16 }}>
      <Text style={st.secTitle}>Top Clients by Revenue ({clientArr.length})</Text>
      {clientArr.length === 0 ? <Text style={{ color: '#94A3B8', textAlign: 'center', padding: 20 }}>No client data</Text>
      : clientArr.map(([name, amt], i) => {
        const count = invoices.filter(inv => (inv.client_name || inv.customer_name) === name).length;
        const paid = invoices.filter(inv => (inv.client_name || inv.customer_name) === name && inv.status === 'paid').reduce((s, inv) => s + parseFloat(inv.total || inv.amount || 0), 0);
        return (<View key={name} style={st.clientCard}>
          <View style={[st.avatar, { backgroundColor: ['#059669', '#3B82F6', '#7C3AED', '#EC4899', '#F59E0B', '#0EA5E9'][i % 6] }]}>
            <Text style={st.avatarTxt}>{(name || '?').substring(0, 2).toUpperCase()}</Text></View>
          <View style={{ flex: 1, marginLeft: 10 }}><Text style={{ fontSize: 14, fontWeight: '600', color: '#1E293B' }}>{name}</Text>
            <Text style={{ fontSize: 11, color: '#94A3B8' }}>{count} invoice{count > 1 ? 's' : ''} · {fmtGBP(paid, 0)} collected</Text></View>
          <View style={{ alignItems: 'flex-end' }}><Text style={{ fontSize: 16, fontWeight: '800', color: C.accent }}>{fmtGBP(amt, 0)}</Text>
            <Text style={{ fontSize: 10, color: '#64748B' }}>total</Text></View></View>);
      })}
    </View>);
  };

  // ── Tab: Recurring ──
  const renderRecurring = () => {
    const recurring = invoices.filter(i => i.is_recurring || ['subscriptions', 'retainer'].includes(i.revenue_source || i.category));
    return (<View style={{ padding: 16 }}>
      <View style={{ flexDirection: 'row', gap: 8, marginBottom: 16 }}>
        <View style={[st.kpiCard, { flex: 1 }]}><MaterialCommunityIcons name="autorenew" size={20} color={C.purple} /><Text style={[st.kpiVal, { color: C.purple }]}>{fmtGBP(stats.mrr, 0)}</Text><Text style={{ fontSize: 10, color: '#94A3B8' }}>Est. MRR</Text></View>
        <View style={[st.kpiCard, { flex: 1 }]}><MaterialCommunityIcons name="calendar-sync" size={20} color={C.accent} /><Text style={[st.kpiVal, { color: C.accent }]}>{fmtGBP(stats.mrr * 12, 0)}</Text><Text style={{ fontSize: 10, color: '#94A3B8' }}>Est. ARR</Text></View>
      </View>
      <Text style={st.secTitle}>Recurring Revenue</Text>
      {recurring.length === 0 ? <View style={st.empty}><MaterialCommunityIcons name="autorenew" size={48} color="#CBD5E1" />
        <Text style={{ fontSize: 14, color: '#94A3B8', marginTop: 12 }}>No recurring revenue tracked yet</Text></View>
      : recurring.map((inv, i) => <InvCard key={inv.id || i} inv={inv} i={i} />)}
    </View>);
  };

  // ── Tab: Forecast ──
  const renderForecast = () => {
    const avgMonthly = stats.monthly.filter(m => m.revenue > 0);
    const avgRev = avgMonthly.length > 0 ? avgMonthly.reduce((s, m) => s + m.revenue, 0) / avgMonthly.length : 0;
    const projectedQ = avgRev * 3;
    const projectedY = avgRev * 12;
    return (<View style={{ padding: 16 }}>
      <Text style={st.secTitle}>Revenue Forecast</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        {[{ l: 'Avg Monthly', v: fmtGBP(avgRev, 0), c: C.accent, i: 'chart-bar' },
          { l: 'Projected Quarter', v: fmtGBP(projectedQ, 0), c: C.blue, i: 'calendar-range' },
          { l: 'Projected Annual', v: fmtGBP(projectedY, 0), c: C.purple, i: 'calendar-clock' },
          { l: 'MRR Projection', v: fmtGBP(stats.mrr * 12, 0), c: '#6366F1', i: 'autorenew' },
        ].map((m, i) => (<View key={i} style={[st.kpiCard, { width: (width - 40) / 2 }]}><MaterialCommunityIcons name={m.i} size={18} color={m.c} />
          <Text style={[st.kpiVal, { color: m.c }]}>{m.v}</Text><Text style={{ fontSize: 10, color: '#94A3B8' }}>{m.l}</Text></View>))}
      </View>
      <View style={st.section}>
        <Text style={st.secTitle}>Cash Flow Projection</Text>
        {[{ l: 'Expected Collections (30d)', v: stats.totalPending, c: C.accent },
          { l: 'Overdue Recovery (est. 60%)', v: stats.totalOverdue * 0.6, c: C.warning },
          { l: 'New Revenue (est.)', v: avgRev, c: C.blue },
          { l: 'Total Projected (30d)', v: stats.totalPending + stats.totalOverdue * 0.6 + avgRev, c: C.accent },
        ].map((m, i) => (<View key={i} style={st.metricRow}><Text style={st.metricLabel}>{m.l}</Text><Text style={[st.metricValue, { color: m.c }]}>{fmtGBP(m.v, 0)}</Text></View>))}
      </View>
      <View style={{ padding: 12, backgroundColor: '#FEF3C7', borderRadius: 10, marginTop: 8 }}>
        <Text style={{ fontSize: 11, color: '#92400E', lineHeight: 16 }}>Forecasts are based on historical averages and current pipeline. Actual results may vary. Review with your accountant for business planning.</Text>
      </View>
    </View>);
  };

  // ── Tab: VAT ──
  const renderVAT = () => {
    const vatOnSales = stats.totalRevenue * 0.2;
    const vatReclaimable = 0; // Would need expenses data
    const netVat = vatOnSales - vatReclaimable;
    return (<View style={{ padding: 16 }}>
      <Text style={st.secTitle}>UK VAT Summary</Text>
      <View style={{ flexDirection: 'row', gap: 8, marginBottom: 16 }}>
        <View style={[st.kpiCard, { flex: 1 }]}><Text style={{ fontSize: 10, color: '#64748B' }}>Output VAT</Text><Text style={[st.kpiVal, { color: C.orange }]}>{fmtGBP(vatOnSales, 0)}</Text></View>
        <View style={[st.kpiCard, { flex: 1 }]}><Text style={{ fontSize: 10, color: '#64748B' }}>Net VAT Due</Text><Text style={[st.kpiVal, { color: C.danger }]}>{fmtGBP(netVat, 0)}</Text></View>
      </View>
      <View style={st.section}>
        <Text style={st.secTitle}>VAT Calculation</Text>
        {[{ l: 'Total Sales (ex-VAT)', v: fmtGBP(stats.totalRevenue), c: C.accent },
          { l: 'Standard Rate (20%)', v: '20%', c: '#64748B' },
          { l: 'Output VAT Collected', v: fmtGBP(vatOnSales), c: C.orange },
          { l: 'Input VAT (from expenses)', v: fmtGBP(vatReclaimable), c: C.accent },
          { l: 'Net VAT Payable to HMRC', v: fmtGBP(netVat), c: C.danger },
        ].map((m, i) => (<View key={i} style={st.metricRow}><Text style={st.metricLabel}>{m.l}</Text><Text style={[st.metricValue, { color: m.c }]}>{m.v}</Text></View>))}
      </View>
      <View style={st.section}>
        <Text style={st.secTitle}>UK VAT Rates</Text>
        {[{ l: 'Standard Rate', v: '20%', d: 'Most goods and services' },
          { l: 'Reduced Rate', v: '5%', d: 'Home energy, child car seats, sanitary products' },
          { l: 'Zero Rate', v: '0%', d: 'Food, books, children\'s clothing, public transport' },
          { l: 'Exempt', v: 'N/A', d: 'Insurance, education, health services, financial services' },
        ].map((r, i) => (<View key={i} style={{ paddingVertical: 8, borderBottomWidth: i < 3 ? 1 : 0, borderBottomColor: '#F1F5F9' }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={{ fontSize: 13, fontWeight: '600', color: '#1E293B' }}>{r.l}</Text><Text style={{ fontSize: 13, fontWeight: '700', color: C.orange }}>{r.v}</Text></View>
          <Text style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>{r.d}</Text></View>))}
      </View>
      <View style={{ padding: 12, backgroundColor: '#DBEAFE', borderRadius: 10, marginTop: 8 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}><MaterialCommunityIcons name="information" size={16} color={C.blue} />
          <Text style={{ fontSize: 12, fontWeight: '700', color: '#1E40AF', marginLeft: 6 }}>VAT Threshold</Text></View>
        <Text style={{ fontSize: 11, color: '#1E40AF', marginTop: 4, lineHeight: 16 }}>The UK VAT registration threshold is £90,000 (2024/25). You must register for VAT once taxable turnover exceeds this in any 12-month rolling period. File quarterly MTD-compliant VAT returns.</Text>
      </View>
    </View>);
  };

  // ── Tab: Settings ──
  const renderSettings = () => (<View style={{ padding: 16 }}>
    <Text style={st.secTitle}>Revenue Settings</Text>
    {[{ l: 'Record New Income', i: 'cash-plus', c: C.accent, fn: () => { setShowAddModal(true); setForm({ revenueSource: 'services', paymentTerms: 'Net 30', vatRate: 20 }); } },
      { l: 'Create Invoice', i: 'file-document-plus', c: C.blue, fn: () => navigation?.navigate('CreateInvoiceScreen', { companyLogo, companyName }) },
      { l: 'View All Invoices', i: 'file-document-multiple', c: C.purple, fn: () => setActiveTab('invoices') },
      { l: 'Export Revenue Report', i: 'export-variant', c: C.teal, fn: exportReport },
      { l: 'Refresh Data', i: 'refresh', c: C.orange, fn: onRefresh },
    ].map((item, i) => (<TouchableOpacity key={i} style={st.settingsRow} onPress={item.fn}>
      <MaterialCommunityIcons name={item.i} size={22} color={item.c} style={{ marginRight: 12 }} />
      <Text style={{ flex: 1, fontSize: 15, fontWeight: '500', color: '#1E293B' }}>{item.l}</Text>
      <MaterialCommunityIcons name="chevron-right" size={20} color="#9CA3AF" /></TouchableOpacity>))}
  </View>);

  // ── Add Income Modal ──
  const renderAddModal = () => (<Modal visible={showAddModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowAddModal(false)}>
    <View style={st.modal}><View style={st.modalHdr}><TouchableOpacity onPress={() => setShowAddModal(false)}><Ionicons name="close" size={24} color="#64748B" /></TouchableOpacity>
      <Text style={st.modalTitle}>Record Income</Text><View style={{ width: 24 }} /></View>
    <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={{ flex: 1 }}>
    <ScrollView style={{ flex: 1, padding: 16 }}>
      {/* Revenue Source */}
      <Text style={st.formLbl}>Revenue Source *</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 14 }} contentContainerStyle={{ gap: 6 }}>
        {REVENUE_SOURCES.map(src => (<TouchableOpacity key={src.id} style={[st.srcChip, form.revenueSource === src.id && { backgroundColor: src.color, borderColor: src.color }]} onPress={() => uf('revenueSource', src.id)}>
          <MaterialCommunityIcons name={src.icon} size={14} color={form.revenueSource === src.id ? '#FFF' : src.color} />
          <Text style={[{ fontSize: 11, color: src.color, fontWeight: '600', marginLeft: 4 }, form.revenueSource === src.id && { color: '#FFF' }]}>{src.label}</Text></TouchableOpacity>))}
      </ScrollView>
      <View style={st.formGroup}><Text style={st.formLbl}>Client Name *</Text><TextInput style={st.formInput} value={form.clientName || ''} onChangeText={v => uf('clientName', v)} placeholder="Client or company name" /></View>
      <View style={st.formGroup}><Text style={st.formLbl}>Client Email</Text><TextInput style={st.formInput} value={form.clientEmail || ''} onChangeText={v => uf('clientEmail', v)} placeholder="client@company.com" keyboardType="email-address" /></View>
      <View style={{ flexDirection: 'row', gap: 8 }}>
        <View style={[st.formGroup, { flex: 1 }]}><Text style={st.formLbl}>Net Amount (£) *</Text><TextInput style={st.formInput} value={form.amount || ''} onChangeText={v => uf('amount', v)} placeholder="0.00" keyboardType="decimal-pad" /></View>
        <View style={[st.formGroup, { flex: 1 }]}><Text style={st.formLbl}>VAT Rate (%)</Text><TextInput style={st.formInput} value={String(form.vatRate || 20)} onChangeText={v => uf('vatRate', parseInt(v) || 0)} keyboardType="numeric" /></View>
      </View>
      {form.amount && parseFloat(form.amount) > 0 && <View style={{ backgroundColor: '#D1FAE5', borderRadius: 10, padding: 12, marginBottom: 14 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={{ fontSize: 12, color: '#065F46' }}>Net:</Text><Text style={{ fontSize: 12, fontWeight: '700', color: '#065F46' }}>{fmtGBP(parseFloat(form.amount))}</Text></View>
        {form.includeVat && <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={{ fontSize: 12, color: '#065F46' }}>VAT ({form.vatRate || 20}%):</Text><Text style={{ fontSize: 12, fontWeight: '700', color: '#065F46' }}>{fmtGBP(parseFloat(form.amount) * ((form.vatRate || 20) / 100))}</Text></View>}
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', borderTopWidth: 1, borderTopColor: '#A7F3D0', paddingTop: 4, marginTop: 4 }}><Text style={{ fontSize: 14, fontWeight: '800', color: '#065F46' }}>Total:</Text><Text style={{ fontSize: 14, fontWeight: '900', color: '#065F46' }}>{fmtGBP(parseFloat(form.amount) * (1 + (form.includeVat ? (form.vatRate || 20) / 100 : 0)))}</Text></View>
      </View>}
      <View style={{ flexDirection: 'row', gap: 8 }}>
        <View style={[st.formGroup, { flex: 1 }]}><Text style={st.formLbl}>Invoice Date</Text><TextInput style={st.formInput} value={form.invoiceDate || ''} onChangeText={v => uf('invoiceDate', v)} placeholder="DD/MM/YYYY" /></View>
        <View style={[st.formGroup, { flex: 1 }]}><Text style={st.formLbl}>Due Date</Text><TextInput style={st.formInput} value={form.dueDate || ''} onChangeText={v => uf('dueDate', v)} placeholder="DD/MM/YYYY" /></View>
      </View>
      <View style={st.formGroup}><Text style={st.formLbl}>Payment Terms</Text>
        <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
          {['Immediate', 'Net 7', 'Net 14', 'Net 30', 'Net 60', 'Net 90'].map(t => (
            <TouchableOpacity key={t} style={[st.chip, form.paymentTerms === t && st.chipActive]} onPress={() => uf('paymentTerms', t)}>
              <Text style={[st.chipTxt, form.paymentTerms === t && { color: '#FFF' }]}>{t}</Text></TouchableOpacity>))}
        </View></View>
      <View style={st.formGroup}><Text style={st.formLbl}>Description</Text><TextInput style={[st.formInput, { height: 60 }]} value={form.description || ''} onChangeText={v => uf('description', v)} placeholder="Service description, project details..." multiline /></View>
      <View style={st.formGroup}><Text style={st.formLbl}>Notes</Text><TextInput style={[st.formInput, { height: 50 }]} value={form.notes || ''} onChangeText={v => uf('notes', v)} placeholder="Internal notes..." multiline /></View>
      <View style={st.switchRow}><Text style={st.switchLbl}>Add VAT to invoice</Text><Switch value={!!form.includeVat} onValueChange={v => uf('includeVat', v)} /></View>
      <View style={st.switchRow}><Text style={st.switchLbl}>Mark as already paid</Text><Switch value={!!form.markPaid} onValueChange={v => uf('markPaid', v)} /></View>
      <View style={st.switchRow}><Text style={st.switchLbl}>Recurring income</Text><Switch value={!!form.isRecurring} onValueChange={v => uf('isRecurring', v)} /></View>
      {form.isRecurring && <View style={st.formGroup}><Text style={st.formLbl}>Recurring Interval</Text>
        <View style={{ flexDirection: 'row', gap: 6 }}>
          {['Weekly', 'Monthly', 'Quarterly', 'Annually'].map(t => (
            <TouchableOpacity key={t} style={[st.chip, form.recurringInterval === t && st.chipActive]} onPress={() => uf('recurringInterval', t)}>
              <Text style={[st.chipTxt, form.recurringInterval === t && { color: '#FFF' }]}>{t}</Text></TouchableOpacity>))}
        </View></View>}
      <TouchableOpacity style={st.primaryBtn} onPress={saveIncome}>
        <MaterialCommunityIcons name="content-save" size={16} color="#FFF" /><Text style={st.primaryBtnTxt}>{form.markPaid ? 'Record Payment' : 'Create Invoice'}</Text></TouchableOpacity>
      <View style={{ height: 40 }} />
    </ScrollView></KeyboardAvoidingView></View></Modal>);

  // ── Detail Modal ──
  const renderDetailModal = () => {
    if (!selectedItem) return null;
    const src = REVENUE_SOURCES.find(s => s.id === (selectedItem.revenue_source || selectedItem.category)) || REVENUE_SOURCES[0];
    const status = INV_STATUS[(selectedItem.status || 'pending').toLowerCase()] || INV_STATUS.pending;
    return (<Modal visible={showDetailModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowDetailModal(false)}>
      <View style={st.modal}><View style={st.modalHdr}><TouchableOpacity onPress={() => setShowDetailModal(false)}><Ionicons name="close" size={24} color="#64748B" /></TouchableOpacity>
        <Text style={st.modalTitle}>Invoice Details</Text><View style={{ width: 24 }} /></View>
      <ScrollView style={{ flex: 1, padding: 16 }}>
        <View style={{ alignItems: 'center', padding: 20, backgroundColor: src.color + '10', borderRadius: 16, marginBottom: 16 }}>
          <MaterialCommunityIcons name={src.icon} size={32} color={src.color} />
          <Text style={{ fontSize: 28, fontWeight: '900', color: src.color, marginTop: 8 }}>{fmtGBP(selectedItem.total || selectedItem.amount)}</Text>
          <Text style={{ fontSize: 15, fontWeight: '600', color: '#334155', marginTop: 4 }}>{selectedItem.client_name || selectedItem.customer_name || 'Client'}</Text>
          <View style={[st.badge, { backgroundColor: status.bg, marginTop: 8 }]}><MaterialCommunityIcons name={status.i} size={12} color={status.c} />
            <Text style={[st.badgeTxt, { color: status.c }]}>{status.l}</Text></View>
        </View>
        <View style={st.detailSection}>
          {[{ l: 'Invoice No.', v: selectedItem.invoice_number || '-' },
            { l: 'Date', v: fmtDate(selectedItem.invoice_date || selectedItem.created_at) },
            { l: 'Due Date', v: fmtDate(selectedItem.due_date) },
            { l: 'Source', v: src.label },
            { l: 'Payment Terms', v: selectedItem.payment_terms || '-' },
            { l: 'Net Amount', v: fmtGBP(selectedItem.amount || selectedItem.total) },
            { l: 'VAT', v: selectedItem.vat_amount ? fmtGBP(selectedItem.vat_amount) + ` (${selectedItem.vat_rate || 20}%)` : '-' },
            { l: 'Total', v: fmtGBP(selectedItem.total || selectedItem.total_amount || selectedItem.amount) },
          ].filter(r => r.v && r.v !== '-').map((r, i) => (<View key={i} style={st.detailRow}><Text style={st.detailLbl}>{r.l}</Text><Text style={st.detailVal}>{r.v}</Text></View>))}
        </View>
        {selectedItem.description && <View style={{ marginBottom: 12 }}><Text style={{ fontSize: 12, fontWeight: '700', color: '#94A3B8', marginBottom: 4 }}>DESCRIPTION</Text><Text style={{ fontSize: 13, color: '#475569', lineHeight: 18 }}>{selectedItem.description}</Text></View>}
        <View style={{ gap: 8 }}>
          {selectedItem.status !== 'paid' && <TouchableOpacity style={[st.primaryBtn, { backgroundColor: C.accent }]} onPress={() => updateInvoiceStatus(selectedItem, 'paid')}>
            <MaterialCommunityIcons name="check-circle" size={16} color="#FFF" /><Text style={st.primaryBtnTxt}>Mark as Paid</Text></TouchableOpacity>}
          {selectedItem.status === 'pending' && <TouchableOpacity style={[st.primaryBtn, { backgroundColor: C.blue }]} onPress={() => updateInvoiceStatus(selectedItem, 'sent')}>
            <MaterialCommunityIcons name="email-fast" size={16} color="#FFF" /><Text style={st.primaryBtnTxt}>Send Reminder</Text></TouchableOpacity>}
          <TouchableOpacity style={[st.primaryBtn, { backgroundColor: '#7C3AED' }]} onPress={() => { setShowDetailModal(false); Share.share({ message: `Invoice: ${selectedItem.invoice_number || ''}\nClient: ${selectedItem.client_name || ''}\nAmount: ${fmtGBP(selectedItem.total || selectedItem.amount)}\nStatus: ${selectedItem.status}\nDate: ${fmtDate(selectedItem.invoice_date || selectedItem.created_at)}` }); }}>
            <MaterialCommunityIcons name="share-variant" size={16} color="#FFF" /><Text style={st.primaryBtnTxt}>Share / Export</Text></TouchableOpacity>
        </View>
        <View style={{ height: 40 }} />
      </ScrollView></View></Modal>);
  };

  // ── Content Router ──
  const renderContent = () => { switch (activeTab) {
    case 'overview': return renderOverview();
    case 'income': return renderIncome();
    case 'invoices': case 'paid': case 'outstanding': return renderInvoices();
    case 'clients': return renderClients();
    case 'recurring': return renderRecurring();
    case 'forecast': return renderForecast();
    case 'vat': return renderVAT();
    case 'settings': return renderSettings();
    default: return renderOverview();
  }};

  if (isLoading) return <SafeAreaView style={st.loading}><StatusBar barStyle="light-content" /><ActivityIndicator size="large" color={C.accent} /><Text style={{ color: '#64748B', marginTop: 12 }}>Loading revenue data...</Text></SafeAreaView>;

  return (<SafeAreaView style={st.container}>
    <StatusBar barStyle="light-content" backgroundColor="#064E3B" />
    {renderHeader()}{renderStats()}{renderTabs()}
    <ScrollView style={{ flex: 1 }} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />} showsVerticalScrollIndicator={false}>
      {renderContent()}<View style={{ height: 80 }} /></ScrollView>
    <TouchableOpacity style={st.fab} onPress={() => { setShowAddModal(true); setForm({ revenueSource: 'services', paymentTerms: 'Net 30', vatRate: 20 }); }}>
      <MaterialCommunityIcons name="cash-plus" size={24} color="#FFF" /></TouchableOpacity>
    {renderAddModal()}{renderDetailModal()}
  </SafeAreaView>);
}

// ══════════════════════════════════════════════════════════════════════════
const st = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F8FAFC' },
  loading: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#F8FAFC' },
  header: { paddingTop: 8, paddingBottom: 12, paddingHorizontal: 16 },
  headerRow: { flexDirection: 'row', alignItems: 'center' },
  hBtn: { width: 36, height: 36, borderRadius: 12, backgroundColor: 'rgba(255,255,255,0.15)', justifyContent: 'center', alignItems: 'center' },
  hTitle: { fontSize: 20, fontWeight: '900', color: '#FFF', letterSpacing: -0.3 },
  statsBar: { flexGrow: 0, backgroundColor: '#FFF', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  statCard: { alignItems: 'center', marginRight: 14, minWidth: 65 },
  statVal: { fontSize: 14, fontWeight: '900', marginTop: 2 },
  statLbl: { fontSize: 9, color: '#94A3B8', marginTop: 1 },
  tabsRow: { backgroundColor: '#FFF', borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  tab: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 9, marginRight: 2, borderRadius: 14 },
  activeTab: { backgroundColor: '#ECFDF5' },
  tabTxt: { fontSize: 11, color: '#94A3B8', marginLeft: 4, fontWeight: '500' },
  kpiRow: { flexDirection: 'row', flexWrap: 'wrap', padding: 16, gap: 8 },
  kpiCard: { width: (width - 40) / 2, backgroundColor: '#FFF', borderRadius: 12, padding: 14, alignItems: 'center', borderWidth: 1, borderColor: '#E2E8F0' },
  kpiVal: { fontSize: 20, fontWeight: '900', marginTop: 2 },
  section: { backgroundColor: '#FFF', marginHorizontal: 16, marginBottom: 12, padding: 16, borderRadius: 14, borderWidth: 1, borderColor: '#E2E8F0' },
  secTitle: { fontSize: 15, fontWeight: '800', color: '#0F172A', marginBottom: 8 },
  alertCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#FEE2E2', marginHorizontal: 16, marginBottom: 8, padding: 12, borderRadius: 10, borderWidth: 1, borderColor: '#FECACA' },
  metricRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#F8FAFC' },
  metricLabel: { fontSize: 13, color: '#475569' },
  metricValue: { fontSize: 13, fontWeight: '700' },
  card: { backgroundColor: '#FFF', marginHorizontal: 16, marginBottom: 6, padding: 12, borderRadius: 12, borderWidth: 1, borderColor: '#E2E8F0' },
  cardIcon: { width: 40, height: 40, borderRadius: 10, justifyContent: 'center', alignItems: 'center' },
  cardName: { fontSize: 14, fontWeight: '700', color: '#1E293B' },
  cardMeta: { fontSize: 11, color: '#94A3B8', marginTop: 1 },
  cardAmount: { fontSize: 16, fontWeight: '900' },
  badge: { flexDirection: 'row', alignItems: 'center', borderRadius: 8, paddingHorizontal: 6, paddingVertical: 2, gap: 3, marginTop: 3 },
  badgeTxt: { fontSize: 9, fontWeight: '700' },
  clientCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#FFF', borderRadius: 10, padding: 12, marginBottom: 6, borderWidth: 1, borderColor: '#E2E8F0' },
  avatar: { width: 38, height: 38, borderRadius: 19, justifyContent: 'center', alignItems: 'center' },
  avatarTxt: { color: '#FFF', fontWeight: '800', fontSize: 13 },
  searchRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#FFF', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 8, margin: 16, marginBottom: 8, borderWidth: 1, borderColor: '#E2E8F0' },
  searchInput: { flex: 1, fontSize: 14, color: '#0F172A', marginLeft: 8 },
  chip: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 14, backgroundColor: '#F1F5F9', borderWidth: 1, borderColor: '#E2E8F0' },
  chipActive: { backgroundColor: '#059669', borderColor: '#059669' },
  chipTxt: { fontSize: 11, color: '#64748B', fontWeight: '600' },
  srcChip: { paddingHorizontal: 10, paddingVertical: 6, borderRadius: 12, borderWidth: 1.5, borderColor: '#E2E8F0', flexDirection: 'row', alignItems: 'center' },
  empty: { alignItems: 'center', padding: 48 },
  settingsRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: '#F3F4F6' },
  modal: { flex: 1, backgroundColor: '#F8FAFC' },
  modalHdr: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14, backgroundColor: '#FFF', borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  modalTitle: { fontSize: 16, fontWeight: '800', color: '#0F172A', flex: 1, textAlign: 'center' },
  formGroup: { marginBottom: 14 },
  formLbl: { fontSize: 13, fontWeight: '600', color: '#334155', marginBottom: 6 },
  formInput: { backgroundColor: '#FFF', borderWidth: 1.5, borderColor: '#E2E8F0', borderRadius: 10, paddingHorizontal: 14, paddingVertical: 12, fontSize: 14, color: '#0F172A' },
  switchRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  switchLbl: { fontSize: 14, color: '#334155', fontWeight: '500' },
  primaryBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#059669', borderRadius: 12, paddingVertical: 14, paddingHorizontal: 20, gap: 6, marginTop: 8 },
  primaryBtnTxt: { color: '#FFF', fontSize: 15, fontWeight: '700' },
  detailSection: { backgroundColor: '#FFF', borderRadius: 12, padding: 14, marginBottom: 16, borderWidth: 1, borderColor: '#E2E8F0' },
  detailRow: { flexDirection: 'row', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#F8FAFC' },
  detailLbl: { width: 110, fontSize: 12, color: '#94A3B8', fontWeight: '600' },
  detailVal: { flex: 1, fontSize: 13, color: '#334155', fontWeight: '500' },
  fab: { position: 'absolute', bottom: 24, right: 20, width: 56, height: 56, borderRadius: 16, backgroundColor: '#059669', alignItems: 'center', justifyContent: 'center', shadowColor: '#059669', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.3, shadowRadius: 8, elevation: 8 },
});
